import IncidentRosterView from 'components/IncidentConfig/IncidentRosterView';
import {
  useGetRosterForIncident,
  useMarkIncidentUserActive,
  useRemoveSelfFromRoster,
  useRemoveMemberFromIncidentRoster,
  useToggleApprovalAuthority,
} from 'hooks/incident/useRoster';
import { useDeleteSchedule } from 'hooks/incident/useSchedule';
import { useCurrentUserApprovalAuthority } from 'hooks/incident/useCurrentUserApprovalAuthority';
import {
  useActualIncidentId,
  useGroupGuid,
} from 'components/IAP/hooks/commonHooks';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import { useGetDraftRosterMembersForIncident } from 'components/IAP/hooks/incidentRoster/useGetDraftRosterMembersForIncident';

const IncidentRosterData = () => {
  const incidentId = useActualIncidentId();
  const groupGuid = useGroupGuid();

  const { data: rosterData = [], isLoading: isRosterLoading } =
    useGetRosterForIncident(incidentId);

  const { data: draftRosterRaw, isLoading: isDraftLoading } =
    useGetDraftRosterMembersForIncident();

  // Treat an empty array while not loading as "no draft roster yet" (null = show CTA).
  // If the backend later returns a distinct signal, this coercion can be revisited.
  const draftRosterData =
    !isDraftLoading && (!draftRosterRaw || draftRosterRaw.length === 0)
      ? null
      : (draftRosterRaw ?? null);
  const { mutate: removeUser } = useRemoveMemberFromIncidentRoster(
    incidentId,
    groupGuid
  );
  const { mutate: removeSelf } = useRemoveSelfFromRoster(incidentId, groupGuid);
  const { mutate: markActive } = useMarkIncidentUserActive(
    incidentId,
    groupGuid
  );
  const { mutate: deleteSchedule } = useDeleteSchedule(incidentId, groupGuid);
  const { mutate: toggleApprovalAuthority, isPending: isTogglingAuthority } =
    useToggleApprovalAuthority(incidentId, groupGuid);

  const currentUserHasApprovalAuthority = useCurrentUserApprovalAuthority();

  if (!incidentId) {
    return null;
  }

  return (
    <IncidentRosterView
      rosterData={rosterData}
      draftRosterData={draftRosterData}
      isRosterLoading={isRosterLoading}
      currentUserHasApprovalAuthority={currentUserHasApprovalAuthority}
      isTogglingAuthority={isTogglingAuthority}
      onRemoveMember={(member) =>
        removeUser({
          user: {
            user_guid: member.userId,
            incident_member_id: member.incidentMemberId,
            email_address: member.emailAddress || '',
          },
        })
      }
      onRemoveSelf={(member) =>
        removeSelf({
          user: {
            user_guid: member.userId,
            incident_member_id: member.incidentMemberId,
            email_address: member.emailAddress || '',
          },
        })
      }
      onMarkActive={(member: RosterEntry) => {
        const userGuid = member.userId;
        const incidentMemberId = member.incidentMemberId;

        markActive({
          user_guid: userGuid,
          incident_member_id: incidentMemberId,
        });
      }}
      onDeleteScheduleConfirmed={({ scheduleId, userGuid, incidentMemberId }) =>
        deleteSchedule({
          scheduleId,
          user_guid: userGuid,
          incident_member_id: incidentMemberId,
        })
      }
      onToggleApprovalAuthority={(member) =>
        toggleApprovalAuthority({
          target_user_guid: member.userId,
          approval_authority: !member.approvalAuthority,
        })
      }
    />
  );
};

export default IncidentRosterData;
